import React, { createContext, useState, useContext, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { hydrateProgress } from "@/lib/progressSync";
import { API_BASE_URL } from "@/lib/apiBase";
import { loadPool } from "@/lib/agentLines";
import ThemeToggle from "@/components/ThemeToggle";

// The two public entry pages stay eager so the first paint has no chunk round-trip. Every gated page
// is code-split — the initial bundle no longer ships all 24 pages, so any given route only downloads
// its own chunk. That's the biggest lever on "opening/reloading a page is slow".
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
const Plan = lazy(() => import("@/pages/Plan"));
const PlanDay = lazy(() => import("@/pages/PlanDay"));
const Coach = lazy(() => import("@/pages/Coach"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Commitment = lazy(() => import("@/pages/Commitment"));
const Roadmap = lazy(() => import("@/pages/Roadmap"));
const SkillPlan = lazy(() => import("@/pages/SkillPlan"));
const Waitlist = lazy(() => import("@/pages/Waitlist"));
const Leads = lazy(() => import("@/pages/Leads"));
const Admin = lazy(() => import("@/pages/Admin"));
const SmartOnboard = lazy(() => import("@/pages/SmartOnboard"));
const SmartQuestions = lazy(() => import("@/pages/SmartQuestions"));
const SmartReady = lazy(() => import("@/pages/SmartReady"));
const Resume = lazy(() => import("@/pages/Resume"));
const JobEval = lazy(() => import("@/pages/JobEval"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const StarStories = lazy(() => import("@/pages/StarStories"));
const Applications = lazy(() => import("@/pages/Applications"));
const LinkedInOutreach = lazy(() => import("@/pages/LinkedInOutreach"));
const Profile = lazy(() => import("@/pages/Profile"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const WeekAssessment = lazy(() => import("@/pages/WeekAssessment"));
const Interview = lazy(() => import("@/pages/Interview"));
const InterviewReport = lazy(() => import("@/pages/InterviewReport"));
const ExtensionConnect = lazy(() => import("@/pages/ExtensionConnect"));
const ExtensionInstall = lazy(() => import("@/pages/ExtensionInstall"));

const UserContext = createContext(null);

export function useUser() {
  return useContext(UserContext);
}

export { UserContext };

const API = `${API_BASE_URL}/api`;

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <Loader2 className="w-5 h-5 text-ink-3 animate-spin" aria-label="Loading" />
    </div>
  );
}

// Replays the page-entrance animation on cross-section navigation. Keyed on the first path segment
// (not the full path) so /plan/week/1/day/1 → day/2 stays mounted — the day arrows shouldn't flash a
// re-entrance, but /dashboard → /plan should.
function RouteTransition({ children }) {
  const { pathname } = useLocation();
  return <div key={"/" + pathname.split("/")[1]} className="route-fade">{children}</div>;
}

function AuthError({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-6">
      <div className="w-full max-w-[340px] text-center">
        <p className="text-h3 font-semibold text-ink">We couldn't finish signing you in</p>
        <p className="text-sm text-ink-2 mt-2">{message}</p>
        <div className="flex gap-2 justify-center mt-6">
          <button
            onClick={() => window.location.reload()}
            className="px-3.5 py-2 rounded-md bg-accent text-accent-fg text-sm font-medium border-none cursor-pointer hover:bg-accent-hover"
          >
            Try again
          </button>
          <button
            onClick={() => supabase.auth.signOut()}
            className="px-3.5 py-2 rounded-md border border-line bg-surface text-sm font-medium text-ink cursor-pointer hover:border-line-strong"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// Gate for the app routes. Public pages ("/", "/login") render for anyone; everything nested under
// this waits for a session and bounces logged-out visitors to /login instead of showing the whole
// app behind a bare login screen (which made "/" open as Login instead of the Landing page).
function RequireAuth() {
  const { session, userId, authError } = useUser();
  if (!session) return <Navigate to="/login" replace />;
  if (userId === null && authError) return <AuthError message={authError} />;
  if (userId === null) return <FullScreenLoader />;   // resolving /auth/me
  return <Outlet />;
}

// Where a completed sign-in should land. Google's redirect is supposed to come back to /login,
// which forwards into the app — but if the redirectTo URL is not in Supabase's allow-list, Supabase
// silently falls back to the project's Site URL, and the user lands on "/" instead. The Landing page
// has no forward logic, so they sat on the marketing page while already signed in; clicking "Login"
// afterwards appeared to work because the session was there all along. That is the reported
// "sometimes I end up on the home page after choosing my Google account".
//
// Keyed on the SIGNED_IN event rather than on merely having a session, so this only fires for an
// actual sign-in — a signed-in user who deliberately opens the landing page is left alone.
const PUBLIC_PATHS = ["/", "/login"];

function PostAuthRedirect({ signedInAt }) {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!signedInAt) return;
    if (PUBLIC_PATHS.includes(location.pathname)) {
      // /dashboard is the gate that OnboardGate then redirects onward from for new users.
      navigate("/dashboard", { replace: true });
    }
  }, [signedInAt, location.pathname, navigate]);
  return null;
}

// New users (signed in but no target role yet) get pushed into onboarding. Lives inside the router so
// it can use navigation. ponytail: one effect, no route wrappers — the redirect is idempotent.
function OnboardGate({ onboarded }) {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (onboarded === false && !location.pathname.startsWith("/onboard")) {
      navigate("/onboard/smart", { replace: true });
    }
  }, [onboarded, location.pathname, navigate]);
  return null;
}

function App() {
  const [session, setSession] = useState(undefined);   // undefined = still checking, null = signed out
  const [userId, setUserId] = useState(null);
  const [onboarded, setOnboarded] = useState(null);
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stride_profile") || "null"); } catch { return null; }
  });
  const [plan, setPlan] = useState(null);

  // Widen the agents' line bank with whatever has been generated into the pool. Fire-and-forget:
  // the authored bank in lib/agentLines.js is complete on its own, so nothing waits on this and a
  // failure is not an error state.
  useEffect(() => { loadPool(API); }, []);

  // Track the Supabase session (Google OAuth). detectSessionInUrl handles the redirect back from Google.
  // SIGNED_IN fires for a real sign-in; INITIAL_SESSION/TOKEN_REFRESHED fire on every load and must
  // NOT trigger the post-auth redirect, or navigating to "/" would bounce a signed-in user away.
  const [signedInAt, setSignedInAt] = useState(0);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s ?? null);
      if (event === "SIGNED_IN" && s) setSignedInAt(Date.now());
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const [authError, setAuthError] = useState(null);

  // Resolve the signed-in user to our int id + pull their saved progress down.
  useEffect(() => {
    if (!session) { setUserId(null); setOnboarded(null); return; }
    let cancelled = false;
    setAuthError(null);
    axios.get(`${API}/auth/me`).then(async ({ data }) => {
      if (cancelled) return;
      setUserId(data.user_id);
      setOnboarded(data.onboarded);
      localStorage.setItem("stride_user_id", String(data.user_id));
      if (data.name) localStorage.setItem("stride_user_name", data.name);
      // Hydrate the nav/profile avatar from the server. Without this the picture only appears after a
      // local Profile save and vanishes on the next login (localStorage is cleared on sign-out).
      const stored = (() => { try { return JSON.parse(localStorage.getItem("stride_profile") || "{}"); } catch { return {}; } })();
      const mergedProfile = { ...stored, name: data.name || stored.name, avatar: data.avatar || stored.avatar || "" };
      localStorage.setItem("stride_profile", JSON.stringify(mergedProfile));
      setProfilePersisted(mergedProfile);
      await hydrateProgress(data.user_id);
    }).catch((e) => {
      // Was `.catch(() => {})` — which left userId null forever and spun the loader with zero clue.
      // A blocked/failed /auth/me (CORS, backend down, missing SUPABASE_JWT_SECRET) is the usual cause.
      if (cancelled) return;
      const detail = e?.response?.data?.detail;
      setAuthError(detail || "Couldn't reach the server. Check your connection and try again.");
    });
    return () => { cancelled = true; };
  }, [session]);

  const setProfilePersisted = (p) => {
    if (p) localStorage.setItem("stride_profile", JSON.stringify(p));
    else localStorage.removeItem("stride_profile");
    setProfile(p);
  };

  if (session === undefined) return <FullScreenLoader />;   // still checking for a session

  return (
    <UserContext.Provider
      value={{ session, userId, setUserId, authError, profile, setProfile: setProfilePersisted, plan, setPlan, onboarded, setOnboarded }}
    >
      <div className="App">
        <BrowserRouter>
          {/* Fallback toggle for pages that render no nav of their own (onboarding, waitlist,
              leads). CSS hides it wherever a nav already carries one, so there is never a pair. */}
          <ThemeToggle className="theme-fallback fixed top-3 right-4 z-[60]" />
          <PostAuthRedirect signedInAt={signedInAt} />
          <OnboardGate onboarded={onboarded} />
          <Suspense fallback={<FullScreenLoader />}>
          <RouteTransition>
          <Routes>
            {/* Public — reachable signed out */}
            <Route path="/" element={<Landing />} />
            {/* A fresh session lands here after Google OAuth → forward into the app. OnboardGate then
                pushes not-yet-onboarded users to /onboard/smart; returning users stay on /dashboard. */}
            <Route path="/login" element={session ? <Navigate to="/dashboard" replace /> : <Login />} />

            {/* Everything below requires a signed-in user (RequireAuth redirects to /login) */}
            <Route element={<RequireAuth />}>
              <Route path="/plan" element={<Plan />} />
              <Route path="/plan/week/:week/day/:day" element={<PlanDay />} />
              <Route path="/coach" element={<Coach />} />
              {/* The chat used to award XP/streak for any message claiming work was done. Check-in
                  now lives on the task page only; this keeps old links and bookmarks working. */}
              <Route path="/checkin" element={<Navigate to="/coach" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/commitment" element={<Commitment />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/skillplan" element={<SkillPlan />} />
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/leads" element={<Leads />} />
              {/* Backend enforces access (require_admin/ADMIN_EMAILS); this only renders it. */}
              <Route path="/admin" element={<Admin />} />
              <Route path="/onboard/smart" element={<SmartOnboard />} />
              <Route path="/onboard/smart/questions" element={<SmartQuestions />} />
              <Route path="/onboard/smart/ready" element={<SmartReady />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/evaluate" element={<JobEval />} />
              <Route path="/interview/stories" element={<StarStories />} />
              <Route path="/applications" element={<Applications />} />
              <Route path="/linkedin" element={<LinkedInOutreach />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/assessment/week/:week" element={<WeekAssessment />} />
              <Route path="/interview" element={<Interview />} />
              <Route path="/interview/report/:reportId" element={<InterviewReport />} />
              <Route path="/extension" element={<ExtensionInstall />} />
              <Route path="/extension/connect" element={<ExtensionConnect />} />
            </Route>
          </Routes>
          </RouteTransition>
          </Suspense>
        </BrowserRouter>
        {/* Vercel Analytics removed: this build is served by Hostinger, where the script it
            fetches (/_vercel/insights/script.js) does not exist. Apache's SPA rewrite answers it
            with index.html, so the browser parsed HTML as JavaScript and threw
            "SyntaxError: Unexpected token '<'" on every single page load. PostHog already covers
            the analytics; this was one wasted request and one console error per visit. */}
      </div>
    </UserContext.Provider>
  );
}

export default App;
